import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Plus } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import { useAuth } from "@/contexts/AuthContext";
import { EvaluationGroupCard } from "@/components/avaliacoes/EvaluationGroupCard";
import { EvaluationGroupDetails } from "@/components/avaliacoes/EvaluationGroupDetails";
import { CreateEvaluationGroupModal } from "@/components/avaliacoes/CreateEvaluationGroupModal";
import type { EvaluationGroupProgress } from "@/types/evaluation";
import { useNavigate, useParams } from "react-router";
import { toUserError } from "@/lib/api-error";

export default function AvaliacoesPerfilFreelancer() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<"pendentes" | "concluidos">("pendentes");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<EvaluationGroupProgress | null>(null);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);
  const { profileName } = useUserPermissions();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const isAdminOrMember = ["admin", "membro"].includes(profileName?.toLowerCase() || "");

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ["evaluation_groups", user?.id, isAdminOrMember],
    queryFn: async () => {
      let query = supabase
        .from("v_evaluation_groups_progress")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      // Se não for admin, filtra apenas os grupos do próprio avaliador
      if (!isAdminOrMember && user) {
        query = query.eq("evaluator_id", user.id);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Erro ao buscar grupos de avaliação:", error);
        throw error;
      }

      return data as EvaluationGroupProgress[];
    },
    enabled: !!user,
  });

  const pendingCount = groups.filter(
    (g) => g.total_freelancers === 0 || g.freelancers_evaluated < g.total_freelancers
  ).length;

  const completedCount = groups.filter(
    (g) => g.total_freelancers > 0 && g.freelancers_evaluated === g.total_freelancers
  ).length;

  const filteredGroups = groups
    .filter((group) => {
      if (activeTab === "pendentes") {
        return group.total_freelancers === 0 || group.freelancers_evaluated < group.total_freelancers;
      }
      return group.total_freelancers > 0 && group.freelancers_evaluated === group.total_freelancers;
    })
    .filter((group) =>
      group.group_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const selectedGroup = id 
    ? groups.find(g => g.group_id === parseInt(id)) 
    : null;

  const handleAddFreelancers = (group: EvaluationGroupProgress) => {
    navigate(
      `/freelancers?evaluationGroup=${group.group_id}&action=evaluation`
    );
  };

  const handleCreateSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["evaluation_groups"] });
  };

  const handleGroupClick = (group: EvaluationGroupProgress) => {
    navigate(`/avaliacoes/perfil-freelancer/${group.group_id}`);
  };

  const handleBack = () => {
    navigate("/avaliacoes/perfil-freelancer");
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;

    setIsDeletingGroup(true);
    try {
      const { error } = await supabase
        .from("evaluation_groups")
        .update({ is_active: false })
        .eq("id", groupToDelete.group_id);

      if (error) throw error;

      toast({
        title: "Grupo deletado",
        description: `O grupo "${groupToDelete.group_name}" foi deletado com sucesso.`,
      });

      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["evaluation_groups"] });

      // Se estiver na página de detalhes do grupo deletado, voltar para lista
      if (selectedGroup?.group_id === groupToDelete.group_id) {
        navigate("/avaliacoes/perfil-freelancer");
      }
    } catch (error: unknown) {
      const errorMessage = toUserError(error).description;
      console.error("Erro ao deletar grupo:", error);
      toast({
        title: "Erro",
        description: "Não foi possível deletar o grupo. " + errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsDeletingGroup(false);
      setGroupToDelete(null);
    }
  };

const renderDeleteDialog = () => (
    <AlertDialog open={!!groupToDelete} onOpenChange={(open) => !open && setGroupToDelete(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Deletar grupo de avaliação</AlertDialogTitle>
          <AlertDialogDescription>
            Tem certeza que deseja deletar o grupo <strong>{groupToDelete?.group_name}</strong>?
            Esta ação não pode ser desfeita e o grupo será marcado como inativo.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeletingGroup}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeleteGroup}
            disabled={isDeletingGroup}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeletingGroup ? "Deletando..." : "Deletar"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (id) {
    if (isLoading) {
      return (
        <>
          <div className="container mx-auto p-6 text-center">Carregando grupo...</div>
          {renderDeleteDialog()}
        </>
      );
    }
    
    if (!selectedGroup) {
      return (
        <>
          <div className="container mx-auto p-6 text-center space-y-4">
            <p className="text-muted-foreground">Grupo não encontrado ou sem permissão.</p>
            <Button onClick={handleBack}>Voltar para lista</Button>
          </div>
          {renderDeleteDialog()}
        </>
      );
    }

    return (
      <>
        <div className="container mx-auto p-6">
          <EvaluationGroupDetails
            group={selectedGroup}
            onBack={handleBack}
            onAddFreelancers={() => handleAddFreelancers(selectedGroup)}
            onDeleteGroup={() => setGroupToDelete(selectedGroup)}
            userProfile={profileName}
          />
        </div>
        {renderDeleteDialog()}
      </>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Avaliações de Perfil Freelancer"
        subtitle="Gerencie seus grupos de avaliação e qualifique os freelancers"
        actions={
          isAdminOrMember && (
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Grupo
            </Button>
          )
        }
      />

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as "pendentes" | "concluidos")}
        className="w-full"
      >
        <TabsList>
          <TabsTrigger value="pendentes">
            Pendentes ({pendingCount})
          </TabsTrigger>
          <TabsTrigger value="concluidos">
            Concluídos ({completedCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar grupos de avaliação..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">Carregando grupos...</div>
      ) : filteredGroups.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {searchTerm
            ? "Nenhum grupo encontrado com este termo."
            : activeTab === "pendentes"
            ? "Nenhum grupo pendente de avaliação."
            : "Nenhum grupo concluído."}
        </div>
      ) : (
        <div className="grid gap-6 grid-cols-1">
          {filteredGroups.map((group) => (
            <EvaluationGroupCard
              key={group.group_id}
              group={group}
              onClick={handleGroupClick}
              onAddFreelancers={isAdminOrMember ? () => handleAddFreelancers(group) : undefined}
              canDelete={isAdminOrMember}
              onDelete={setGroupToDelete}
            />
          ))}
        </div>
      )}

      <CreateEvaluationGroupModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {renderDeleteDialog()}
    </div>
  );
}
